<!-- BaseButton.vue (admin) -->
<template tag="관리자 버튼 컴포넌트">
  <template v-if="isSettingsMode">
    <div class="d-flex justify-end">
      <template v-for="(button, _index) in buttonList" :key="_index">
        <v-btn
          v-if="fnGetButtonView(button) && !button.hideButton"
          :ref="(el) => setButtonRef(el, button.id)"
          :color="button.color"
          :disabled="button.disabled"
          :variant="button.variant || 'flat'"
          class="ml-1"
          @click="() => onClickButton(button)"
        >
          {{ button.name }}
        </v-btn>
      </template>
    </div>
  </template>
  <template v-else>
    <v-btn :icon="icon" :color="props.color" :variant="props.variant">
      <v-icon v-if="icon">{{ icon }}</v-icon>
      <slot></slot>
    </v-btn>
  </template>
</template>

<script setup lang="ts">
import { computed, ref, type ComponentPublicInstance } from "vue";
import { i18n, global } from "@/app/plugins/global";
import type { ButtonTypes, ButtonConfig } from "@/common/types/ButtonTypes";

/* ------------------------------------------------------------
                          Vue Instance & Global Properties
   ------------------------------------------------------------ */
const { $text } = i18n();
const vm = global();

/* ------------------------------------------------------------
                          Props
   ------------------------------------------------------------ */
const props = defineProps<{
  /** 버튼 설정 */
  settings?: ButtonTypes;
  icon?: string;
  /** 버튼 색상 */
  color?: string;
  /** 버튼 variant */
  variant?: "flat" | "outlined" | "elevated" | "tonal" | "text";
}>();

/* ------------------------------------------------------------
                          Variable Declarations
   ------------------------------------------------------------ */
const buttonRefs = ref<Record<string, HTMLElement>>({});

/* ------------------------------------------------------------
                          Computed Properties
   ------------------------------------------------------------ */
/** 버튼 목록 생성 */
const isSettingsMode = computed(() => !!props.settings?.buttons);

/** 버튼 목록 가져오기 */
const buttonList = computed<ButtonConfig[]>(() => {
  if (!props.settings || !props.settings.buttons) {
    return [];
  }
  const { buttons } = props.settings;

  return buttons.map((button) => ({
    ...button,
    color: button.color || fnGetButtonColor(button.type || button.id) || "default",
    name: button.name || fnGetButtonName(button.id),
    variant: button.variant || fnGetButtonVariant(button.type || button.id),
  }));
});

/* ------------------------------------------------------------
                          Methods
   ------------------------------------------------------------ */
/** 버튼 요소 참조 설정 */
const setButtonRef = (el: Element | ComponentPublicInstance | null, buttonId: string) => {
  if (el) {
    const htmlEl = (el as ComponentPublicInstance).$el || (el as HTMLElement);
    buttonRefs.value[buttonId] = htmlEl;
  }
};

/* ------------------------------------------------------------
                          Methods
   ------------------------------------------------------------ */
/** 기본 버튼 이름 반환 */
const fnGetButtonName = (buttonId: string): string => {
  switch (buttonId) {
    case "SAVE":
      return $text("button.save");
    case "UPDATE":
      return $text("button.update");
    case "RESET":
      return $text("button.reset");
    case "CANCEL":
      return $text("button.cancel");
    case "CLOSE":
      return $text("button.close");
    case "DELETE":
      return $text("button.del");
    case "USE_Y":
      return $text("button.useY");
    case "USE_N":
      return $text("button.useN");
    case "PREV":
      return $text("button.prev");
    case "NEXT":
      return $text("button.next");
    case "LIST":
      return $text("button.list");
    case "REGISTER":
      return $text("button.register");
    case "RETURN":
      return $text("button.return");
    case "APPROVE":
      return $text("button.approve");
    default:
      return buttonId;
  }
};

/** 기본 버튼 표출 여부*/
const fnGetButtonView = (button: ButtonConfig): boolean => {
  // 권한 체크 우회 옵션이 있으면 항상 표시
  if (button.skipAuthCheck) {
    return true;
  }

  switch (button.id) {
    case "SAVE":
    case "REGISTER":
      return vm.$checkAuthC();
    case "UPDATE":
      return vm.$checkAuthU();
    case "DELETE":
      return vm.$checkAuthD();
    case "USE_Y":
      return vm.$checkAuthU();
    case "USE_N":
      return vm.$checkAuthU();
    default:
      return true;
  }
};

/** 버튼 클릭 핸들러 */
const onClickButton = async (button: ButtonConfig) => {
  if (!props.settings || !props.settings.buttons) {
    return;
  }
  const { action } = button;
  const {
    formData,
    initialData,
    saveApi,
    statusApi,
    onTabChange,
    currentTab,
    totalTabs,
    formField,
  } = props.settings;

  try {
    if (typeof action === "function") {
      // action 함수의 파라미터 개수에 따라 적절한 인자 전달
      if (action.length > 0) {
        await action(formData);
      } else {
        await action();
      }
    } else if (typeof action === "string") {
      // 기본 액션 실행
      switch (action) {
        case "save":
        case "register":
        case "update": {
          if (saveApi && formData) {
            await saveApi(formData);
          }
          break;
        }

        case "reset": {
          if (formData && initialData) {
            vm.$toast($text("message.resetSuccess"));
            Object.assign(formData, initialData);
            formField?.forEach((field) => {
              if (field.type === "file") {
                field.referFiles = [];
              }
            });
          }
          break;
        }

        case "cancel": {
          vm.$router.back();
          break;
        }

        case "delete": {
          vm.$confirm(
            $text("confirm.delete"),
            $text("confirm.title"),
            async (isConfirm: boolean) => {
              if (isConfirm && statusApi && formData) {
                await statusApi("delete", formData);
              }
            }
          );
          break;
        }

        case "useY": {
          vm.$confirm($text("confirm.useY"), $text("confirm.title"), async (isConfirm: boolean) => {
            if (isConfirm && statusApi && formData) {
              await statusApi("useY", formData);
            }
          });
          break;
        }

        case "useN": {
          vm.$confirm($text("confirm.useN"), $text("confirm.title"), async (isConfirm: boolean) => {
            if (isConfirm && statusApi && formData) {
              await statusApi("useN", formData);
            }
          });
          break;
        }

        case "prevTab": {
          const prevTab = Number(currentTab) - 1;
          if (prevTab >= 1 && onTabChange) {
            onTabChange(prevTab.toString());
          }
          break;
        }

        case "nextTab": {
          const nextTab = Number(currentTab) + 1;
          if (nextTab <= (totalTabs || 1) && onTabChange) {
            onTabChange(nextTab.toString());
          }
          break;
        }

        default:
          // Default action not handled
          break;
      }
    }
  } catch (error) {
    console.error("Button action error:", error);
    vm.$toast($text("message.error"), "error");
  }
};

/** 버튼 타입별 색상 반환 */
const fnGetButtonColor = (type: string): string => {
  switch (type) {
    case "DELETE":
    case "CANCEL":
    case "CLOSE":
      return "error";
    case "USE_Y":
    case "USE_N":
      return "success";
    default:
      return "primary";
  }
};

/** 버튼 타입별 버튼 스타일 반환 */
const fnGetButtonVariant = (type: string): "flat" | "outlined" | "elevated" | "tonal" | "text" => {
  switch (type) {
    case "PREV":
    case "NEXT":
    case "CANCEL":
    case "CLOSE":
    case "RESET":
    case "USE_Y":
    case "USE_N":
    case "LIST":
      return "outlined";
    default:
      return "flat";
  }
};

/* ------------------------------------------------------------
                          Expose
   ------------------------------------------------------------ */
defineExpose({
  getButtonEl: (buttonId: string) => buttonRefs.value[buttonId],
});
</script>
